import { randomUUID } from "#/shared/utils/string";

// Used to hold analytics events until it is worth sending them, so a pageview that records a view, a click and a search
// opens one connection rather than three. A batch goes out once `sizeLimit` events are held, once `timeLimit` has
// passed, or when the page is left. With an outbox, each event is also stored there as it is appended, in a batch this
// page holds, so a page that dies before a flush leaves its events for the next page.
//
// Each event keeps the tokens it was appended under, as `ahoy.js` stamps its own events with the visit current when
// they are tracked. A flush sends one batch for each set of tokens, so an event appended before a visit expired is
// sent under that visit rather than the one after it.
//
// Decision record: https://github.com/combinaut/sparkle/wiki/ADR-0029-Batch-Analytics-Events-in-Our-Own-Modules-Around-a-Vendored-Ahoy-JS
export class AnalyticsEventQueue {
  // @param send {Function} called, once for each set of tokens, with an array of `{id, name, properties, time}` events,
  //   the tokens they were appended under, and the outbox key they are stored under, or undefined when they are not
  // @param options {Object}
  //   - `sizeLimit`: the events held before a flush
  //   - `timeLimit`: the milliseconds they are held for
  //   - `tokens`: returns the `{visit_token, visitor_token}` to keep with an event as it is appended. Without it,
  //     every event is sent with undefined tokens.
  //   - `outbox`: the `AnalyticsEventOutbox` that stores the appended events. Without it, they are kept in memory only.
  constructor(send, options) {
    options = options || {};

    this.send = send;
    this.sizeLimit = options.sizeLimit || 10;
    this.timeLimit = options.timeLimit || 30000;
    this.tokens = options.tokens || function () {};
    this.outbox = options.outbox;
    this.appendedEvents = [];
    this.storedKeys = new Map();
    this.timer = null;
  }

  // Returns the event as it was queued.
  // @param time {Number} the milliseconds since the epoch at which the event happened, now when omitted
  append(name, properties, time) {
    const event = this.event(name, properties, time);
    const tokens = this.tokens();

    this.appendedEvents.push({ event: event, tokens: tokens });
    this.storeAppended(tokens);

    if (this.appendedEvents.length >= this.sizeLimit) {
      this.flush();
    } else if (!this.timer) {
      this.timer = setTimeout(() => this.flush(), this.timeLimit);
    }

    return event;
  }

  // Sends everything held. A second call resends nothing.
  flush() {
    const appended = this.appendedEvents;
    const storedKeys = this.storedKeys;

    this.appendedEvents = [];
    this.storedKeys = new Map();

    this.deliver(appended, storedKeys);
  }

  // Stores the events appended under `tokens` as one held batch, replacing the one stored for them so far. The copy
  // stored so far is removed only once its replacement is stored, so when the outbox cannot store the larger batch, the
  // earlier events stay stored and the rest are kept in memory only, until the next append tries again. Does nothing
  // without an outbox.
  storeAppended(tokens) {
    if (!this.outbox) {
      return;
    }

    const group = JSON.stringify(tokens);
    const batch = {
      events: this.appendedEvents
        .filter((appended) => JSON.stringify(appended.tokens) === group)
        .map((appended) => appended.event),
      tokens: tokens,
    };
    const key = this.storedKeys.get(group);

    if (key && this.outbox.rewrite(key, batch)) {
      return;
    }

    const heldKey = this.outbox.hold(batch);

    if (!heldKey) {
      return;
    }

    if (key) {
      this.outbox.remove(key);
    }

    this.storedKeys.set(group, heldKey);
  }

  // The time an event carries is the moment it happened, not the moment its batch went out. The `id` fills the
  // field `ahoy.js` sets on its own events. Ahoy hands it to the store as `event_id`, and `ahoy_events` has no column
  // for it, so it is not saved.
  event(name, properties, time) {
    return {
      id: randomUUID(),
      name: name,
      properties: properties,
      time: (time || Date.now()) / 1000.0,
    };
  }

  // Sends `appended` as one batch for each set of tokens, in the order each set was first appended, with the outbox key
  // `storedKeys` has for that set. A batch keeps its events in the order they were appended.
  deliver(appended, storedKeys) {
    clearTimeout(this.timer);
    this.timer = null;

    const batches = new Map();

    appended.forEach(({ event, tokens }) => {
      const key = JSON.stringify(tokens);

      if (!batches.has(key)) {
        batches.set(key, { events: [], tokens: tokens, storedKey: storedKeys && storedKeys.get(key) });
      }

      batches.get(key).events.push(event);
    });

    batches.forEach((batch) => this.send(batch.events, batch.tokens, batch.storedKey));
  }
}
